<script lang="ts" setup>
  import { defineProps, ref, provide, type PropType, onBeforeUnmount, defineEmits } from 'vue'
  import { Trash2, Upload } from 'lucide-vue-next'
  import {
    type CustomerCase,
    DocumentsService,
    UploadOriginEnum,
    type Document
  } from '@/types/generated'

  const emit = defineEmits<{
    (e: 'fileUploaded', documentUuid: string): void
    (e: 'fileAiReady', documentUuid: string): void
    (e: 'fileAiComplete', documentUuid: string): void
  }>()

  const props = defineProps({
    caseData: {
      type: Object as PropType<CustomerCase>,
      required: true
    },
    maxSize: {
      type: Number,
      default: 100
    },
    autoRemoveAfterUpload: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after successful upload'
    },
    autoRemoveAfterAiReady: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after AI processing stage 1 is done'
    },
    autoRemoveAfterAiComplete: {
      type: Boolean,
      default: false,
      description: 'Whether to automatically remove files after AI processing is complete'
    }
  })

  interface UploadingFile {
    file: File
    progress: number
    uploading: boolean
    error?: string
    document?: Document
    isProcessing?: boolean
    currentProcess?: string
    processStartedAt?: string
    elapsedTime?: string
    elapsedTimer?: number
    isAiReady?: boolean
    isAiComplete?: boolean
  }

  const uploadingFiles = ref<UploadingFile[]>([])
  const dropZone = ref<HTMLDivElement>()
  const pollingIntervals = ref<{ [key: string]: number }>({})

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  const formatElapsedTime = (startTime: string): string => {
    const start = new Date(startTime).getTime()
    const now = new Date().getTime()
    const elapsed = Math.floor((now - start) / 1000) // seconds

    if (elapsed < 60) {
      return `${elapsed}s`
    }
    const minutes = Math.floor(elapsed / 60)
    const seconds = elapsed % 60
    return `${minutes}m ${seconds}s`
  }

  const getThumbnailUrl = (file: UploadingFile) => {
    return file.document?.images?.find((img) => img.image_preset === 'thumbnail')?.url
  }

  const updateElapsedTime = (fileEntry: UploadingFile) => {
    if (!fileEntry.processStartedAt) return

    const start = new Date(fileEntry.processStartedAt).getTime()
    const now = new Date().getTime()
    const elapsed = (now - start) / 1000 // seconds

    if (elapsed < 60) {
      fileEntry.elapsedTime = elapsed.toFixed(1) + 's'
    } else {
      const minutes = Math.floor(elapsed / 60)
      const seconds = (elapsed % 60).toFixed(1)
      fileEntry.elapsedTime = `${minutes}m ${seconds}s`
    }
  }

  const startTimer = (fileEntry: UploadingFile) => {
    if (fileEntry.elapsedTimer) {
      clearInterval(fileEntry.elapsedTimer)
    }

    updateElapsedTime(fileEntry)
    fileEntry.elapsedTimer = window.setInterval(() => {
      updateElapsedTime(fileEntry)
    }, 100)
  }

  const stopTimer = (fileEntry: UploadingFile) => {
    if (fileEntry.elapsedTimer) {
      clearInterval(fileEntry.elapsedTimer)
      fileEntry.elapsedTimer = undefined
    }
  }

  const startPolling = (documentUuid: string, fileEntry: UploadingFile) => {
    if (pollingIntervals.value[documentUuid]) return

    pollingIntervals.value[documentUuid] = window.setInterval(async () => {
      try {
        const doc = await DocumentsService.documentsRetrieve({
          uuid: documentUuid
        })

        fileEntry.isProcessing =
          doc.needs_ocr ||
          doc.needs_embeddings ||
          doc.needs_summary ||
          doc.needs_summary_embedding ||
          doc.needs_analysis

        const previousProcess = fileEntry.currentProcess
        const previousStartTime = fileEntry.processStartedAt

        // Determine current process and its start time
        if (doc.ocr_started_at && !doc.ocr_finished_at) {
          fileEntry.currentProcess = 'OCR'
          fileEntry.processStartedAt = doc.ocr_started_at
        } else if (doc.embeddings_started_at && !doc.embeddings_finished_at) {
          fileEntry.currentProcess = 'Embeddings'
          fileEntry.processStartedAt = doc.embeddings_started_at
        } else if (doc.summary_started_at && !doc.summary_finished_at) {
          fileEntry.currentProcess = 'Summary'
          fileEntry.processStartedAt = doc.summary_started_at
        } else if (doc.summary_embedding_started_at && !doc.summary_embedding_finished_at) {
          fileEntry.currentProcess = 'Summary Embedding'
          fileEntry.processStartedAt = doc.summary_embedding_started_at
        } else if (doc.analysis_started_at && !doc.analysis_finished_at) {
          fileEntry.currentProcess = 'Analysis'
          fileEntry.processStartedAt = doc.analysis_started_at
        } else {
          fileEntry.currentProcess = undefined
          fileEntry.processStartedAt = undefined
        }

        // Check AI ready state (OCR and Embeddings complete)
        const wasAiReady = fileEntry.isAiReady
        fileEntry.isAiReady = doc.ocr_is_finished && doc.embeddings_is_finished

        // Emit AI ready event when state changes
        if (!wasAiReady && fileEntry.isAiReady) {
          emit('fileAiReady', documentUuid)
        }

        // Check AI complete state (all processes complete)
        const wasAiComplete = fileEntry.isAiComplete
        fileEntry.isAiComplete =
          doc.ocr_is_finished &&
          doc.embeddings_is_finished &&
          doc.summary_is_finished &&
          doc.summary_embedding_is_finished &&
          doc.analysis_is_finished

        // Emit AI complete event when state changes
        if (!wasAiComplete && fileEntry.isAiComplete) {
          emit('fileAiComplete', documentUuid)
        }

        // Handle auto-removal based on AI states
        if (!wasAiReady && fileEntry.isAiReady && props.autoRemoveAfterAiReady) {
          setTimeout(() => {
            const index = uploadingFiles.value.findIndex((f) => f.file === fileEntry.file)
            if (index !== -1) {
              uploadingFiles.value.splice(index, 1)
            }
          }, 1000)
          return
        }

        if (!wasAiComplete && fileEntry.isAiComplete && props.autoRemoveAfterAiComplete) {
          setTimeout(() => {
            const index = uploadingFiles.value.findIndex((f) => f.file === fileEntry.file)
            if (index !== -1) {
              uploadingFiles.value.splice(index, 1)
            }
          }, 1000)
          return
        }

        // If process changed or started, restart timer
        if (
          fileEntry.processStartedAt &&
          (previousProcess !== fileEntry.currentProcess ||
            previousStartTime !== fileEntry.processStartedAt)
        ) {
          startTimer(fileEntry)
        }

        // If all processing is done, stop polling and timer
        if (!fileEntry.isProcessing) {
          stopPolling(documentUuid)
          stopTimer(fileEntry)
        }
      } catch (error) {
        console.error('Error polling document status:', error)
        stopPolling(documentUuid)
        stopTimer(fileEntry)
      }
    }, 2000)
  }

  const stopPolling = (documentUuid: string) => {
    if (pollingIntervals.value[documentUuid]) {
      clearInterval(pollingIntervals.value[documentUuid])
      delete pollingIntervals.value[documentUuid]
    }
  }

  onBeforeUnmount(() => {
    // Clear all polling intervals and timers when component is unmounted
    Object.keys(pollingIntervals.value).forEach(stopPolling)
    uploadingFiles.value.forEach(stopTimer)
  })

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const files = e.dataTransfer?.files
    if (files) {
      handleFiles(Array.from(files))
    }
  }

  const onFileInput = (e: Event) => {
    const input = e.target as HTMLInputElement
    if (input.files) {
      handleFiles(Array.from(input.files))
    }
  }

  const handleFiles = (files: File[]) => {
    files.forEach((file) => {
      if (file.size > props.maxSize * 1024 * 1024) {
        // TODO: Show error message
        return
      }

      uploadingFiles.value.push({
        file,
        progress: 0,
        uploading: false
      })
      uploadFile(file)
    })
  }

  const uploadFile = async (file: File) => {
    const fileEntry = uploadingFiles.value.find((f) => f.file === file)
    if (!fileEntry) return

    fileEntry.uploading = true
    fileEntry.error = undefined

    try {
      const response = await DocumentsService.documentsUploadCreate({
        caseUuid: props.caseData.uuid,
        formData: {
          file: file,
          upload_origin: UploadOriginEnum.MANUELL_HINZUGEFÜGT
        }
      })

      fileEntry.document = response
      fileEntry.progress = 100
      fileEntry.isProcessing = true

      // Emit upload complete event
      emit('fileUploaded', response.uuid)

      if (props.autoRemoveAfterUpload) {
        setTimeout(() => {
          const index = uploadingFiles.value.findIndex((f) => f.file === file)
          if (index !== -1) {
            uploadingFiles.value.splice(index, 1)
          }
        }, 1000)
      } else {
        // Start polling for document status
        startPolling(response.uuid, fileEntry)
      }
    } catch (error: any) {
      console.error('Upload failed:', error)
      fileEntry.error = error?.body?.file?.[0] || error?.body?.detail || 'Upload failed'
      fileEntry.progress = 0
    } finally {
      fileEntry.uploading = false
    }
  }

  const removeFile = (file: File) => {
    const index = uploadingFiles.value.findIndex((f) => f.file === file)
    if (index !== -1) {
      uploadingFiles.value.splice(index, 1)
    }
  }

  const preventDefaults = (e: Event) => {
    e.preventDefault()
    e.stopPropagation()
  }
</script>

<style scoped>
  .pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
</style>

<template>
  <div class="mb-4 space-y-2 empty:mb-0">
    <template
      v-for="file in uploadingFiles"
      :key="file.file.name"
    >
      <div class="rounded-xl border border-solid border-gray-300 bg-white p-3">
        <div class="mb-1 flex items-center justify-between">
          <div class="flex items-center gap-x-3">
            <span
              class="flex size-10 items-center justify-center rounded-lg border border-gray-200 text-gray-500"
            >
              <img
                v-if="file.document"
                :src="getThumbnailUrl(file)"
                class="rounded-lg"
              />
            </span>
            <div>
              <p class="text-sm font-medium text-gray-800">
                <span class="inline-block max-w-[300px] truncate align-bottom">
                  {{ file.document?.title || file.file.name }}
                </span>
              </p>
              <p class="text-xs text-gray-500">
                {{ formatFileSize(file.file.size) }}
                <template v-if="file.isProcessing && file.document && file.currentProcess">
                  <span class="mx-1.5">•</span>
                  <span class="pulse text-purple-600">{{ file.currentProcess }}</span>
                  <span
                    v-if="file.elapsedTime"
                    class="ml-1 text-gray-400"
                  >
                    ({{ file.elapsedTime }})
                  </span>
                </template>
              </p>
              <p
                v-if="file.error"
                class="text-xs text-red-500"
              >
                {{ file.error }}
              </p>
            </div>
          </div>

          <div class="flex items-center gap-x-2">
            <button
              type="button"
              class="text-gray-500 hover:text-gray-800 focus:text-gray-800 focus:outline-none"
              @click="removeFile(file.file)"
            >
              <Trash2 class="size-4 shrink-0" />
            </button>
          </div>
        </div>

        <div class="flex items-center gap-x-3 whitespace-nowrap">
          <div
            class="flex h-2 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            :aria-valuenow="file.progress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="flex flex-col justify-center overflow-hidden whitespace-nowrap rounded-full bg-purple-600
                text-center text-xs text-white transition-all duration-500"
              :style="{ width: `${file.progress}%` }"
            ></div>
          </div>
          <div class="w-10 text-end">
            <span class="text-sm text-gray-800">
              <span>{{ file.progress }}</span>
              %
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>

  <div
    ref="dropZone"
    @dragover.prevent="preventDefaults"
    @dragenter.prevent="preventDefaults"
    @dragleave.prevent="preventDefaults"
    @drop="onDrop"
    @click="$refs.fileInput?.click()"
    class="flex cursor-pointer justify-center rounded-xl border border-dashed border-gray-300 bg-white p-12"
  >
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      @change="onFileInput"
      multiple
    />
    <div class="text-center">
      <span
        class="inline-flex size-16 items-center justify-center rounded-full bg-gray-100 text-gray-800"
      >
        <Upload class="size-6 shrink-0" />
      </span>

      <div class="mt-4 flex flex-wrap justify-center text-sm leading-6 text-gray-600">
        <span class="pe-1 font-medium text-gray-800">Drop your file here or</span>
        <span
          class="rounded-lg bg-white font-semibold text-purple-600 decoration-2 focus-within:outline-none
            focus-within:ring-2 focus-within:ring-purple-600 focus-within:ring-offset-2 hover:text-purple-700
            hover:underline"
        >
          browse
        </span>
      </div>

      <p class="mt-1 text-xs text-gray-400">Pick a file up to {{ props.maxSize }}MB.</p>
    </div>
  </div>
</template>
